<template>
  <!-- "Your sky made you this" (recipient polish 2026-11-03, DESIGN.md §15,
       §20.4; prototype `keepCard`). Shown right after the birth time (or "I
       don't know it"), in the same sheet. The name comes first; the portrait
       circle shimmers while the sky paints it, and fades in when it is ready.
       Keep stays dim until the portrait is painted; "Keep going, I'll come
       back to it" closes the sheet meanwhile. Someone who already chose their
       own name is offered the portrait only. -->
  <div class="skc" data-testid="sky-keep-card">
    <div class="skc-ey">The Rising</div>
    <h3 class="skc-h">{{ portraitOnly ? 'Your sky painted you this' : 'Your sky made you this' }}</h3>
    <div class="skc-por" :class="{ painting: !painted }" data-testid="skc-portrait">
      <img v-if="painted" :src="gift.portrait" alt="Your portrait, painted from your chart" class="fade" />
      <span v-else class="skc-spark" aria-hidden="true">✦</span>
    </div>
    <div v-if="!portraitOnly && gift.name" class="skc-name" data-testid="quest-sky-name">{{ gift.name }}</div>
    <template v-if="!painted">
      <p class="skc-line" data-testid="skc-painting"><BreathDots label="Your sky is painting your portrait…" /></p>
      <p class="skc-p">{{ portraitOnly ? 'It appears here as soon as it’s painted, and you can keep going meanwhile.'
        : 'Your name is ready. The portrait appears here as soon as it’s painted, and you can keep going meanwhile.' }}</p>
    </template>
    <p v-else class="skc-p">{{ portraitOnly
      ? 'From your birthday, your sky painted this portrait. Keep it, and it becomes the picture others see here. Or paint a new one now.'
      : 'From your birthday, your sky painted this portrait and chose this name. Keep them, and they become the name and picture others see here. Or change either one now.' }}</p>
    <button type="button" class="skc-go" data-testid="quest-keep-sky" :disabled="busy || !painted" @click="emit('keep')">
      {{ busy ? '…' : portraitOnly ? '✦ Keep this portrait' : '✦ Keep this name and portrait' }}
    </button>
    <div class="skc-alts">
      <button v-if="!painted" type="button" data-testid="skc-later" @click="emit('later')">Keep going, I’ll come back to it</button>
      <template v-else>
        <template v-if="!portraitOnly"><button type="button" data-testid="skc-rename" @click="emit('rename')">Choose another name</button>
          <span aria-hidden="true">·</span></template>
        <button type="button" data-testid="skc-repaint" @click="emit('repaint')">Paint a new portrait</button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BreathDots from '@/components/ui/BreathDots.vue'

const props = defineProps({
  // nav.sky_gift: { state, name, portrait }.
  gift: { type: Object, default: () => ({}) },
  // They already chose their own name: the portrait only.
  portraitOnly: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
})
const emit = defineEmits(['keep', 'later', 'rename', 'repaint'])
const painted = computed(() => !!(props.gift && props.gift.portrait && props.gift.state !== 'painting'))
</script>

<style scoped>
.skc { text-align: center; color: #efe6ff; }
.skc-ey { font: 600 10.5px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: .2em; text-transform: uppercase; color: #e7c983; }
.skc-h { margin: 6px 0 10px; font: 500 26px 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #fff; }
.skc-por { width: 132px; height: 132px; margin: 0 auto; border-radius: 50%; overflow: hidden; display: grid; place-items: center;
  border: 2px solid rgba(240,217,163,.6); box-shadow: 0 0 40px rgba(240,217,163,.2);
  background: radial-gradient(circle, rgba(240,217,163,.18), rgba(123,75,176,.25) 60%, #1d0f3a); }
.skc-por img { width: 100%; height: 100%; object-fit: cover; }
.skc-por img.fade { animation: skc-in .9s ease both; }
.skc-por.painting { animation: skc-shimmer 2.4s ease-in-out infinite; }
.skc-spark { font-size: 30px; color: #f0d9a3; }
@keyframes skc-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes skc-shimmer { 0%, 100% { box-shadow: 0 0 24px rgba(240,217,163,.15); } 50% { box-shadow: 0 0 48px rgba(240,217,163,.45); } }
.skc-name { margin-top: 10px; font: 600 20px 'Letter Display', 'Cinzel', Georgia, serif; color: #fff1cf; }
.skc-line { margin: 10px 0 0; }
.skc-p { margin: 8px auto 0; max-width: 420px; font: 400 15px/1.5 Inter, system-ui, sans-serif; color: #d8c9f2; }
.skc-go { margin-top: 16px; min-height: 48px; padding: 12px 24px; border: 0; border-radius: 999px; cursor: pointer;
  font: 800 14.5px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: .04em; color: #2a1a05;
  background: linear-gradient(180deg, #f5d98a, #d9a93f); }
.skc-go:disabled { filter: grayscale(.6); opacity: .45; cursor: default; }
.skc-alts { margin-top: 10px; display: flex; justify-content: center; align-items: center; gap: 6px; flex-wrap: wrap; color: #9b86c9; }
.skc-alts button { min-height: 44px; padding: 0 6px; background: none; border: 0; color: #d8c9f2; cursor: pointer;
  font: 600 13.5px Inter, system-ui, sans-serif; text-decoration: underline; text-underline-offset: 3px; }
.skc-go:focus-visible, .skc-alts button:focus-visible { outline: 2px solid #f0d9a3; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .skc-por.painting, .skc-por img.fade { animation: none; } }
</style>
